import * as XLSX from 'xlsx';
import { Customer } from '../types/Customer';

export interface ExcelCustomerRow {
  name: string;
  email: string;
  phone?: string;
  notes?: string;
}

/**
 * Parse Excel file and extract customer data
 * Expected columns: name, email, phone (optional), notes (optional)
 */
export const parseCustomersFromExcel = (file: File): Promise<ExcelCustomerRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          raw: false,
          defval: '' 
        });

        // Map to our customer format
        const customers: ExcelCustomerRow[] = [];
        
        for (const row of jsonData) {
          const rowData = row as Record<string, any>;
          
          // Try different column name variations (case-insensitive)
          const getName = () => {
            const nameKeys = ['name', 'Name', 'NAME', 'CustomerName', 'Full Name', 'fullname'];
            for (const key of nameKeys) {
              if (rowData[key]) return String(rowData[key]).trim();
            }
            return '';
          };

          const getEmail = () => {
            const emailKeys = ['email', 'Email', 'EMAIL', 'CustomerEmail', 'E-mail', 'e-mail'];
            for (const key of emailKeys) {
              if (rowData[key]) return String(rowData[key]).toLowerCase().trim();
            }
            return '';
          };

          const getPhone = () => {
            const phoneKeys = ['phone', 'Phone', 'PHONE', 'CustomerPhone', 'Tel', 'Telephone', 'Mobile'];
            for (const key of phoneKeys) {
              if (rowData[key]) {
                const phone = String(rowData[key]).trim();
                // Keep phone as-is, user wants to preserve formats
                return phone || undefined;
              }
            }
            return undefined;
          };

          const getNotes = () => {
            const notesKeys = ['notes', 'Notes', 'NOTES', 'Note', 'Comments', 'Remarks'];
            for (const key of notesKeys) {
              if (rowData[key]) return String(rowData[key]).trim();
            }
            return undefined;
          };

          const name = getName();
          const email = getEmail();
          const phone = getPhone();
          const notes = getNotes();

          // Validate required fields
          if (name && email) {
            // Basic email validation
            if (email.includes('@')) {
              customers.push({
                name,
                email,
                phone,
                notes,
              });
            }
          }
        }

        if (customers.length === 0) {
          reject(new Error('No valid customer data found in Excel file. Please check column names (name, email, phone).'));
          return;
        }

        resolve(customers);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Export customers to Excel file
 */
export const exportCustomersToExcel = (customers: Customer[], filename: string = 'customers.xlsx') => {
  // Prepare data for export
  const exportData = customers.map((customer) => ({
    'Name': customer.name,
    'Email': customer.email,
    'Phone': customer.phone || '',
    'Total Payments': customer.totalPayments,
    'Total Amount (EUR)': customer.totalAmount.toFixed(2),
    'Last Payment': customer.lastPaymentDate 
      ? new Date(customer.lastPaymentDate.toDate()).toLocaleDateString('tr-TR')
      : '',
    'Created At': new Date(customer.createdAt.toDate()).toLocaleDateString('tr-TR'),
    'Created By': customer.createdBy || '',
    'Notes': customer.notes || '',
    'Deleted': customer.deleted ? 'Yes' : 'No',
  }));

  // Create workbook
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');

  // Set column widths
  worksheet['!cols'] = [
    { wch: 25 }, // Name
    { wch: 30 }, // Email
    { wch: 20 }, // Phone
    { wch: 15 }, // Total Payments
    { wch: 18 }, // Total Amount
    { wch: 15 }, // Last Payment
    { wch: 15 }, // Created At
    { wch: 25 }, // Created By
    { wch: 30 }, // Notes
    { wch: 10 }, // Deleted
  ];

  // Export
  XLSX.writeFile(workbook, filename);
};

/**
 * Validate and prepare customer data for import
 * Checks for duplicates and invalid data
 */
export const validateCustomerImport = (
  excelCustomers: ExcelCustomerRow[],
  existingCustomers: Customer[]
): {
  valid: ExcelCustomerRow[];
  duplicates: { email: string; name: string; reason: string }[];
  invalid: { email: string; name: string; reason: string }[];
} => {
  const valid: ExcelCustomerRow[] = [];
  const duplicates: { email: string; name: string; reason: string }[] = [];
  const invalid: { email: string; name: string; reason: string }[] = [];

  // Create map of existing emails
  const existingEmails = new Set(
    existingCustomers
      .filter(c => !c.deleted)
      .map(c => c.email.toLowerCase())
  );

  // Track emails in this import
  const importEmails = new Set<string>();

  for (const customer of excelCustomers) {
    const emailLower = customer.email.toLowerCase();

    // Check if already exists in database
    if (existingEmails.has(emailLower)) {
      duplicates.push({
        email: customer.email,
        name: customer.name,
        reason: 'Already exists in database',
      });
      continue;
    }

    // Check if duplicate in this import
    if (importEmails.has(emailLower)) {
      duplicates.push({
        email: customer.email,
        name: customer.name,
        reason: 'Duplicate in Excel file',
      });
      continue;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customer.email)) {
      invalid.push({
        email: customer.email,
        name: customer.name,
        reason: 'Invalid email format',
      });
      continue;
    }

    // Validate name
    if (customer.name.length < 2) {
      invalid.push({
        email: customer.email,
        name: customer.name,
        reason: 'Name too short (minimum 2 characters)',
      });
      continue;
    }

    // Valid customer
    importEmails.add(emailLower);
    valid.push(customer);
  }

  return { valid, duplicates, invalid };
};
